import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { PlannerPriorityType, ResRobotFetchTrip, ResRobotStopLocation, ResRobotTrip } from '@ridango/components';
import { PlannerFacade } from '@ridango/components/lib/schedule/+state/planner/planner.facade';
import { BaseComponent, DateBuilder } from '@ridango/core';

import { Angulartics2 } from 'angulartics2';
import momenttz from 'moment-timezone';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'rwidget-schedule-planner',
    templateUrl: './planner.component.html'
})
export class SchedulePlannerComponent extends BaseComponent implements OnInit, OnDestroy {
    @Output() onPlannerSearch = new EventEmitter<any>();

    origins$: Observable<ResRobotStopLocation[]> = this.plannerFacade.origins$;
    destinations$: Observable<ResRobotStopLocation[]> = this.plannerFacade.destinations$;

    selectedOrigin$: Observable<ResRobotStopLocation> = this.plannerFacade.selectedOrigin$;
    selectedDestination$: Observable<ResRobotStopLocation> = this.plannerFacade.selectedDestination$;
    selectedDate$: Observable<Date> = this.plannerFacade.selectedDate$;
    selectedPriority$: Observable<number> = this.plannerFacade.selectedPriority$;

    trips$: Observable<ResRobotTrip[]> = this.plannerFacade.trips$;
    tripsLoading$: Observable<boolean> = this.plannerFacade.tripsLoading$;

    selectedOriginId;
    selectedDestinationId;

    planRideForm: FormGroup;
    origin: FormControl;
    destination: FormControl;
    trip: ResRobotFetchTrip;
    date = new Date();
    priorityOptions = PlannerPriorityType;

    constructor(
        @Inject('ENV') public env: any,
        private plannerFacade: PlannerFacade,
        private router: Router,
        private translate: TranslateService,
        private angulartics2: Angulartics2
    ) {
        super();
    }

    ngOnInit() {
        this.origin = new FormControl();
        this.destination = new FormControl();
        this.planRideForm = new FormGroup({
            originFeature: new FormControl(null, [Validators.required]),
            destinationFeature: new FormControl(null, [Validators.required]),
            date: new FormControl(''),
            time: new FormControl(''),
            priority: new FormControl(0, [Validators.required])
        });

        this.origin.valueChanges
            .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.componentDestroyed$))
            .subscribe((value) => {
                if (value && value.length > 0) {
                    this.plannerFacade.searchOrigin(value);
                }
            });

        this.destination.valueChanges
            .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.componentDestroyed$))
            .subscribe((value) => {
                if (value && value.length > 0) {
                    this.plannerFacade.searchDestination(value);
                }
            });

        this.selectedOrigin$.pipe(takeUntil(this.componentDestroyed$)).subscribe((origin) => {
            this.planRideForm.controls.originFeature.patchValue(origin);
        });

        this.selectedDestination$.pipe(takeUntil(this.componentDestroyed$)).subscribe((destination) => {
            this.planRideForm.controls.destinationFeature.patchValue(destination);
        });

        this.selectedDate$.pipe(takeUntil(this.componentDestroyed$)).subscribe((date) => {
            if (date) {
                this.updateDateField(date);
                this.planRideForm.controls.time.patchValue({ hour: date.getHours(), minute: date.getMinutes() });
            }
        });

        this.selectedPriority$.pipe(takeUntil(this.componentDestroyed$)).subscribe((priority) => {
            if (priority !== null && priority !== undefined) {
                this.planRideForm.controls.priority.patchValue(priority);
            }
        });

        combineLatest([this.selectedOrigin$, this.selectedDestination$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.submit();
            });
    }

    changePriority(value: number) {
        this.planRideForm.controls.priority.patchValue(value);
    }

    suggestionsDisplayCallback = (suggestion) =>
        suggestion && (suggestion.name || this.translate.instant('LOCATION_FROM_MAP'));

    originSearched(value) {
        this.origin.setValue(value);
    }

    destinationSearched(value) {
        this.destination.setValue(value);
    }

    originSelected(stop) {
        this.plannerFacade.setOrigin(stop);
    }

    destinationSelected(stop) {
        this.plannerFacade.setDestination(stop);
    }

    selectTrip(index: number) {
        this.plannerFacade.setSelectedTripId(index);
        this.router.navigate([this.translate.currentLang, 'schedule', 'planner', 'trip', index]);
    }

    updateDateField(date: Date) {
        this.date = date;
    }

    submit() {
        if (this.planRideForm.valid) {
            this.onPlannerSearch.emit();
            this.angulartics2.eventTrack.next({
                action: 'journeyPlannerSearch',
                properties: { category: 'Journey Planner' }
            });

            this.resetTrips();

            const priority = this.planRideForm.controls.priority.value;
            this.plannerFacade.setPriority(priority);

            this.trip = {
                date: this.getDate(),
                priority: priority
            };

            const origin = this.planRideForm.controls.originFeature.value;
            if (origin.id) {
                this.trip = { ...this.trip, originId: +origin.extId };
            } else {
                this.trip = { ...this.trip, originLat: +origin.lat, originLng: +origin.lon };
            }

            const destination = this.planRideForm.controls.destinationFeature.value;
            if (destination.id) {
                this.trip = { ...this.trip, destId: +destination.extId };
            } else {
                this.trip = { ...this.trip, destLat: +destination.lat, destLng: +destination.lon };
            }

            this.plannerFacade.fetchTrip(this.trip);
        }
    }

    getDate() {
        const timeValue = this.planRideForm.value.time;
        if (timeValue) {
            const dateBuilder = new DateBuilder(this.date)
                .setTimeStartOfDay()
                .addHours(timeValue.hour)
                .addMinutes(timeValue.minute);
            this.date = dateBuilder.getDate();
        }
        const date = momenttz.tz(this.date, 'Europe/Tallinn');
        this.plannerFacade.setDate(date.toDate());

        return date;
    }

    resetTrips() {
        this.plannerFacade.resetTrips();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
